import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Check, Sparkles, Zap, Crown, Loader2, 
  CheckCircle, ArrowRight, CreditCard,
  Package, Users, BarChart3, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = {
  free: Sparkles,
  standard: Zap,
  pro: Crown,
  enterprise: Building2
};

const PLAN_COLORS = {
  free: 'from-gray-500 to-gray-600',
  standard: 'from-blue-500 to-indigo-600',
  pro: 'from-purple-500 to-pink-600',
  enterprise: 'from-amber-500 to-orange-600'
};

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(false);
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [extraUserPrice, setExtraUserPrice] = useState(10);
  const [stripeEnabled, setStripeEnabled] = useState(false);

  // Handle success/error from URL params
  const success = searchParams.get('success');
  const cancelled = searchParams.get('cancelled');
  const sessionId = searchParams.get('session_id');

  // Poll for payment status after redirect from Stripe
  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setPollingStatus(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/billing/stripe/checkout/status/${sid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        toast.success(`Successfully subscribed to ${response.data.plan_id} plan!`);
        setPollingStatus(false);
        // Clean up URL params
        setSearchParams({});
        // Refresh data
        fetchData();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPollingStatus(false);
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  }, [setSearchParams]);

  useEffect(() => {
    if (success === 'true' && sessionId) {
      setPollingStatus(true);
      toast.info('Verifying payment...');
      pollPaymentStatus(sessionId);
    } else if (cancelled === 'true') {
      toast.info('Payment was cancelled');
      setSearchParams({});
    }
    fetchData();
  }, [success, cancelled, sessionId, pollPaymentStatus, setSearchParams]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch from both billing endpoints
      const [shopifyPlansRes, usageRes, stripePlansRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/shopify-billing/plans`).catch(() => ({ data: { plans: [] } })),
        axios.get(`${API_BASE_URL}/api/shopify-billing/usage`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_BASE_URL}/api/billing/stripe/plans`).catch(() => ({ data: { plans: [], stripe_enabled: false } }))
      ]);

      // Prefer Stripe plans if available, fallback to Shopify
      const stripePlans = stripePlansRes.data.plans || [];
      const shopifyPlans = shopifyPlansRes.data.plans || [];
      
      // Merge plans, preferring Stripe data
      const mergedPlans = stripePlans.length > 0 ? stripePlans : shopifyPlans;
      
      // Render every plan returned by the platform — so a new plan
      // created in the super-admin Plans page automatically shows up
      // here. Enterprise (and any future custom plan) is included so
      // tenants currently ON that plan can see what they have. The
      // upgrade CTA for plans the merchant can't self-serve is rendered
      // as "Contact Sales" downstream (see the plan card render).
      setPlans(mergedPlans);
      setExtraUserPrice(shopifyPlansRes.data.extra_user_price || 10);
      setStripeEnabled(stripePlansRes.data.stripe_enabled || false);
      
      if (usageRes.data) {
        setUsage(usageRes.data);
        setCurrentPlan(usageRes.data.plan?.type || 'free');
      }
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleStripeUpgrade = async (planId) => {
    if (planId === currentPlan || planId === 'free') return;

    setUpgrading(planId);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${API_BASE_URL}/api/billing/stripe/checkout/create`,
        {
          plan_id: planId,
          origin_url: originUrl
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Upgrade failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to initiate checkout');
      setUpgrading(false);
    }
  };

  const handleShopifyUpgrade = async (planId) => {
    if (planId === currentPlan || planId === 'free') return;

    setUpgrading(planId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/shopify-billing/upgrade?plan_type=${planId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Shopify approval page
      window.location.href = response.data.confirmation_url;
    } catch (err) {
      console.error('Upgrade failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to initiate upgrade');
      setUpgrading(false);
    }
  };

  const handleUpgrade = (planId) => {
    // Use Stripe by default if enabled, otherwise try Shopify
    if (stripeEnabled) {
      handleStripeUpgrade(planId);
    } else {
      // For now, use Stripe even if not "configured" since we have env key
      handleStripeUpgrade(planId);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (limit >= 999999) return 0; // Unlimited
    return Math.min(Math.round((used / limit) * 100), 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-wt-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg p-6" data-testid="billing-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Plans & Billing
          </h1>
          <p className="text-gray-400">
            Manage your subscription and view usage
          </p>
        </div>

        {/* Payment Processing Status */}
        {pollingStatus && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
            <p className="text-blue-300">Verifying your payment... Please wait.</p>
          </div>
        )}

        {/* Current Usage */}
        {usage && (
          <div className="bg-wt-surface rounded-xl p-6 mb-8 border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-wt-primary" />
              Current Usage
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {/* Orders */}
              <div className="bg-wt-bg rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-300 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Orders
                  </span>
                  <span className="text-white font-medium">
                    {(usage.usage?.orders?.used || 0).toLocaleString()} / {(usage.usage?.orders?.limit || 100).toLocaleString()}
                  </span>
                </div>
                <Progress 
                  value={getUsagePercentage(usage.usage?.orders?.used || 0, usage.usage?.orders?.limit || 100)} 
                  className={`h-2 ${getUsageColor(getUsagePercentage(usage.usage?.orders?.used || 0, usage.usage?.orders?.limit || 100))}`}
                />
                {(usage.usage?.orders?.extra || 0) > 0 && (
                  <p className="text-xs text-yellow-400 mt-2">
                    +{usage.usage.orders.extra} extra orders (${(usage.usage.orders.extra_charged || 0).toFixed(2)} charged)
                  </p>
                )}
              </div>

              {/* Leads */}
              <div className="bg-wt-bg rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-300 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Leads
                  </span>
                  <span className="text-white font-medium">
                    {(usage.usage?.leads?.used || 0).toLocaleString()} / {(usage.usage?.leads?.limit || 100).toLocaleString()}
                  </span>
                </div>
                <Progress 
                  value={getUsagePercentage(usage.usage?.leads?.used || 0, usage.usage?.leads?.limit || 100)} 
                  className={`h-2 ${getUsageColor(getUsagePercentage(usage.usage?.leads?.used || 0, usage.usage?.leads?.limit || 100))}`}
                />
              </div>

              {/* Team */}
              <div className="bg-wt-bg rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-300 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Team Members
                  </span>
                  <span className="text-white font-medium">
                    {usage.usage?.users?.used || 0} / {usage.usage?.users?.limit || 2}
                  </span>
                </div>
                <Progress 
                  value={getUsagePercentage(usage.usage?.users?.used || 0, usage.usage?.users?.limit || 2)} 
                  className="h-2"
                />
              </div>
            </div>

            {/* Billing Cycle */}
            <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Current Plan</p>
                <p className="text-white font-semibold capitalize flex items-center gap-2">
                  {usage.plan?.name || 'Free'}
                  <Badge className="bg-wt-primary/20 text-wt-primary text-xs">
                    ${usage.plan?.price || 0}/mo
                  </Badge>
                </p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Billing Cycle Ends</p>
                <p className="text-white font-semibold">
                  {usage.billing_cycle?.end 
                    ? new Date(usage.billing_cycle.end).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Plans */}
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          Choose Your Plan
          <Badge className="bg-green-500/20 text-green-400 text-xs">
            <CreditCard className="w-3 h-3 mr-1" />
            Secure Checkout
          </Badge>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const PlanIcon = PLAN_ICONS[plan.id] || Sparkles;
            const isCurrentPlan = currentPlan === plan.id;
            const isPro = plan.id === 'pro';
            const isFree = plan.id === 'free';
            // Plans flagged for sales contact OR explicit Enterprise tier
            // render a "Contact Sales" CTA instead of Stripe checkout.
            // This keeps custom-priced plans visible (so the merchant can
            // see what they're on if they're already there) without
            // exposing them as self-serve upgrades.
            const isContactSales = plan.contact_sales || plan.is_custom || plan.id === 'enterprise';

            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`
                  relative bg-wt-surface rounded-xl border overflow-hidden transition-all
                  ${isPro ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-white/10'}
                  ${isCurrentPlan ? 'ring-2 ring-green-500/50' : ''}
                  hover:border-white/20
                `}
              >
                {/* Popular Badge */}
                {isPro && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-br-lg">
                    CURRENT
                  </div>
                )}

                <div className="p-6">
                  {/* Plan Header */}
                  <div className={`
                    w-12 h-12 rounded-lg bg-gradient-to-r ${PLAN_COLORS[plan.id] || PLAN_COLORS.free} 
                    flex items-center justify-center mb-4
                  `}>
                    <PlanIcon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-1">
                    {plan.name}
                  </h3>
                  
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-white">
                      ${plan.price}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-gray-400">/month</span>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Extra Order Price */}
                  {plan.extra_order_price > 0 && (
                    <p className="text-xs text-gray-400 mb-4">
                      Extra orders: ${plan.extra_order_price}/order
                    </p>
                  )}

                  {/* CTA Button */}
                  <Button
                    onClick={() => {
                      if (isContactSales && !isCurrentPlan) {
                        // Pop a mailto with a pre-filled subject for sales.
                        window.location.href =
                          `mailto:sales@opswarex.com?subject=${encodeURIComponent('Plan upgrade enquiry: ' + plan.name)}`;
                        return;
                      }
                      handleUpgrade(plan.id);
                    }}
                    disabled={isCurrentPlan || isFree || upgrading || pollingStatus}
                    data-testid={`upgrade-btn-${plan.id}`}
                    className={`
                      w-full ${isPro 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' 
                        : isCurrentPlan 
                          ? 'bg-green-600 cursor-default'
                          : isFree
                            ? 'bg-gray-600 cursor-default'
                            : isContactSales
                              ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                              : 'bg-wt-primary hover:bg-wt-primary/90'}
                    `}
                  >
                    {upgrading === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Current Plan
                      </>
                    ) : isFree ? (
                      'Free Forever'
                    ) : isContactSales ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Contact Sales
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add-ons */}
        <div className="mt-8 bg-wt-surface rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">
            Add-ons
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Extra Team Members</p>
              <p className="text-gray-400 text-sm">
                Add more team members beyond your plan limit
              </p>
            </div>
            <div className="text-right">
              <p className="text-white font-bold text-lg">
                ${extraUserPrice}/user/month
              </p>
            </div>
          </div>
        </div>

        {/* FAQ or Support */}
        <div className="mt-8 text-center">
          <p className="text-gray-400">
            Questions about billing?{' '}
            <a href="mailto:support@opswarex.app" className="text-wt-primary hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
